import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Divider,
  SimpleGrid,
  useToast,
  Spinner,
  Center
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdDownload, MdPictureAsPdf, MdTableChart } from 'react-icons/md';
import type { Dictamen } from '../../views/Dictamenes/types';
import { ReporteService } from '../../services/reporteService';
import type { EstadisticasReporte } from '../../services/reporteService';

interface VistaReporteProps {
  isOpen: boolean;
  onClose: () => void;
  dictamenes: Dictamen[];
  filtros: any;
  rangoFechas: string;
}

export default function VistaReporte({
  isOpen,
  onClose,
  dictamenes,
  filtros,
  rangoFechas
}: VistaReporteProps) {
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [generandoExcel, setGenerandoExcel] = useState(false);
  const toast = useToast();

  const estadisticas = ReporteService.calcularEstadisticas(dictamenes);
  const fechaGeneracion = new Date().toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const handleDescargarPDF = async () => {
    try {
      setGenerandoPDF(true);
      const blob = await ReporteService.generarPDF(dictamenes, filtros);
      ReporteService.descargarPDF(blob);
      
      toast({
        title: 'PDF generado exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error al generar PDF',
        description: 'Intente nuevamente',
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      setGenerandoPDF(false);
    }
  };

  const handleDescargarExcel = () => {
    try {
      setGenerandoExcel(true);
      ReporteService.generarExcel(dictamenes, filtros);
      
      toast({
        title: 'Excel generado exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Error al generar Excel',
        description: 'Intente nuevamente',
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      setGenerandoExcel(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="bold">Vista Previa del Reporte</Text>
            <Text fontSize="sm" opacity={0.9}>Dictámenes Emitidos</Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton color="white" />
        
        <ModalBody p={6}>
          <VStack spacing={6} align="stretch">
            {/* Header del reporte */}
            <Box textAlign="center" py={4} bg="gray.50" borderRadius="md">
              <Text fontSize="2xl" fontWeight="bold" color="#5b182b" mb={2}>
                REPORTE DE DICTÁMENES EMITIDOS
              </Text>
              <Text fontSize="md" color="gray.600" mb={2}>
                H. Ayuntamiento de Totolac, Tlaxcala
              </Text>
              <Text fontSize="lg" fontWeight="semibold" color="#bb8c52">
                {rangoFechas}
              </Text>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Generado el {fechaGeneracion}
              </Text>
            </Box>

            {/* Estadísticas */}
            <Box bg="#5b182b0a" p={4} borderRadius="md" border="1px solid" borderColor="#5b182b20">
              <Text fontSize="lg" fontWeight="bold" color="#5b182b" mb={4} textAlign="center">
                DESGLOSE
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <HStack justify="space-between" p={3} bg="white" borderRadius="md" shadow="sm">
                  <Text fontWeight="semibold">TOTAL DE DICTÁMENES EMITIDOS:</Text>
                  <Badge bg="#bb8c52" color="white" fontSize="md" px={3} py={1} borderRadius="full">
                    {estadisticas.totalDictamenes}
                  </Badge>
                </HStack>
                <HStack justify="space-between" p={3} bg="white" borderRadius="md" shadow="sm">
                  <Text fontWeight="semibold">DICTÁMENES DE RIESGO:</Text>
                  <Badge bg="#5b182b" color="white" fontSize="md" px={3} py={1} borderRadius="full">
                    {estadisticas.dictamenesRiesgo}
                  </Badge>
                </HStack>
                <HStack justify="space-between" p={3} bg="white" borderRadius="md" shadow="sm">
                  <Text fontWeight="semibold">DICTÁMENES DE HABITABILIDAD:</Text>
                  <Badge bg="#691f30" color="white" fontSize="md" px={3} py={1} borderRadius="full">
                    {estadisticas.dictamenesHabitabilidad}
                  </Badge>
                </HStack>
                <HStack justify="space-between" p={3} bg="white" borderRadius="md" shadow="sm">
                  <Text fontWeight="semibold">DICTÁMENES DE SEGURIDAD:</Text>
                  <Badge bg="#681a2d" color="white" fontSize="md" px={3} py={1} borderRadius="full">
                    {estadisticas.dictamenesSeguridad}
                  </Badge>
                </HStack>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Tabla de datos */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="#5b182b" mb={4}>
                Registros Incluidos ({dictamenes.length})
              </Text>
              
              {dictamenes.length === 0 ? (
                <Box textAlign="center" py={8} bg="gray.50" borderRadius="md">
                  <Text color="gray.500">No hay registros para mostrar</Text>
                </Box>
              ) : (
                <Box overflowX="auto" maxH="400px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
                  <Table size="sm" variant="striped">
                    <Thead bg="#5b182b" position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th color="white" fontSize="xs">Folio</Th>
                        <Th color="white" fontSize="xs">Tipo</Th>
                        <Th color="white" fontSize="xs">Solicitante</Th>
                        <Th color="white" fontSize="xs">Fecha</Th>
                        <Th color="white" fontSize="xs">Operador</Th>
                        <Th color="white" fontSize="xs">Monto</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {dictamenes.map((dictamen, index) => (
                        <Tr key={index}>
                          <Td fontSize="xs">{dictamen.folio || `ID-${dictamen.id}`}</Td>
                          <Td fontSize="xs">
                            <Badge 
                              size="sm" 
                              bg="#bb8c52"
                              color={"white"}
                            >
                              {dictamen.tipo || 'N/A'}
                            </Badge>
                          </Td>
                          <Td fontSize="xs">{dictamen.nombre || 'N/A'}</Td>
                          <Td fontSize="xs">
                            {dictamen.fechaSolicitud 
                              ? new Date(dictamen.fechaSolicitud).toLocaleDateString('es-ES')
                              : 'N/A'
                            }
                          </Td>
                          <Td fontSize="xs">{dictamen.operador || 'N/A'}</Td>
                          <Td fontSize="xs" fontWeight="semibold" color="#bb8c52">
                            ${Number(dictamen.montoPago || 0).toFixed(2)}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter bg="gray.50" borderBottomRadius="md">
          <HStack spacing={3}>
            <Button
              leftIcon={generandoPDF ? <Spinner size="sm" /> : <MdPictureAsPdf />}
              bg="#5b182b"
              color="white"
              _hover={{ bg: "#691f30" }}
              onClick={handleDescargarPDF}
              isLoading={generandoPDF}
              loadingText="Generando PDF..."
              isDisabled={dictamenes.length === 0}
            >
              Descargar PDF
            </Button>
            
            <Button
              leftIcon={generandoExcel ? <Spinner size="sm" /> : <MdTableChart />}
              bg="#bb8c52"
              color="white"
              _hover={{ bg: "#a67c47" }}
              onClick={handleDescargarExcel}
              isLoading={generandoExcel}
              loadingText="Generando Excel..."
              isDisabled={dictamenes.length === 0}
            >
              Descargar Excel
            </Button>
            
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}